<template>
    <div class="container">
        <h1>Comment choisir une Stake Pool pour déléguer ses ADAs</h1>

        <!--
        <p>Page en cours de rédaction. Ça arrive très vite !</p>
        -->

        <p class="intro">
            Choisir une Stake Pool pour déléguer ses ADAs est un processus plus complexe qu'il n'y parait. Quelles sont les stake pools qui rapportent
            le plus d'intérêt ? Quelles sont celles qui servent et desservent Cardano ?
            Explications sur les récompenses de staking de Cardano pour un investissement rentable, bio et sans gluten.
        </p>

        <h2>Définir votre catégorie d'investisseur</h2>
        <p>
            C'est plus important qu'il n'y parait. Si vous êtes un détenteur d'ADA court-termiste, et que vous voulez mettre à profit vos ADAs
            en attendant une opportunité du marché pour les vendre, vous allez vouloir choisir une stake pool qui offre le meilleur rapport d'intérêt,
            sans vous soucier du reste. Si au contraire, vous êtes renseigné sur le projet Cardano (ce site est là pour vous aider), et que (comme nous)
            vous <b>voulez miser sur le long terme</b>, alors le choix est plus compliqué: <b>vous devez vous soucier du vote que représente votre stake</b>.
        </p>

        <h2>D'où viennent les récompenses de Staking</h2>

        <p>Il est important, pour bien choisir sa Stake Pool, de comprendre d'où viennent les récompenses.</p>

        <p>
            L'intrégralité du protocole de délagation de Ouroboros (le protocole de consensus d'ADA) est défini dans
            <a href="https://hydra.iohk.io/build/3744897/download/1/delegation_design_spec.pdf" title="Design de la délégation de Cardano">Ce whitepaper</a>.
            Si vous n'avez pas la foi de lire un document de 50 pages en anglais, on va en résumer le principal rapidement (en simplifiant un peu).
        </p>

        <p>
            Les transactions de la blockchain sont divisées en <b>époques</b>, une époque étant actuellement définie à <b>5 jours</b>. A la fin de chaque
            époque, les Stake Pools (voir <a href="/doc-cardano/qu-est-ce-qu-une-stake-pool/">Qu'est ce qu'une stake pool ?</a>) ayant créé des blocs <b>
                récupèrent des récompenses à partager avec leurs délégateurs pour chaque bloc miné</b>. Le droit de créer un bloc se gagne dans une
            énorme lotterie, ou
            chaque Stake Pool possède autant de tickets que d'ADAs qui lui sont délégués. Les grosses Stake Pools récupèrent donc plus de blocs et plus
            régulièrement, ce qui fluidifie la distribution des récompenses pour les délégateurs, à l'inverse des plus petites qui peuvent ne récupérer
            un bloc que toutes les 5, ou 10 époques par exemple, mais elles partagent les récompenses avec plus d'utilisateurs.
        </p>

        <p>
            Attention, il y a un piège: Cardano ne veut pas que les Stake Pools puissent devenir énorme (sinon, le protocole sera de facto centralisé),
            donc Ouroboros définit une limite de saturation (actuellement fixée à 1% des ADAs mis en jeu sur le réseau). Passé cette limite, la Stake
            Pool n'augmentera plus ses probabilité de droit de création de bloc (et donc, ses récompenses). Ses délégateurs auront donc tout intérêt
            à aller se trouver une autre Stake Pool.
        </p>

        <h2>Comment sont réparties les récompenses de staking ?</h2>
        <p>
            Une fois les récompenses de blocs récupérées vient le temps du partage. Les Stake Pools couvrent leur frais et réalisent leur gain en
            s'attribuant une partie des récompenses. Lors de leur publication sur la blockchain, elles déclarent une partie <b>fixe</b> et une partie
            <b>variable</b> qu'elle préleveront lors du partage (la partie fixe couvrant les frais fixes, la partie variable servant à incentiver,
            mais ce n'est pas une convention nécessairement respectée).
            Ces données, pour chaque Stake Pool, sont visibles sur les différents portefeuilles qui autorisent le staking (Daedalus, Yoroi, Adalite, etc)
            mais aussi sur les sites utilitaires comme <a href="https://adapools.org">Adapools</a> ou <a href="https://pooltool.io">PoolTool</a>
        </p>

        <p>
            Voici comment se déroule le partage des récompenses:
        </p>
        <ol>
            <li>
                D'abord, les Stake Pools récupèrent leurs <b>frais fixes</b> : Ce montant fixe correspond (en théorie) aux coûts de fonctionnement de
                la Stake Pool (machines, etc). Cardano fixe une limite basse de <b>340 Adas</b> (68 Adas par jour).
            </li>
            <li>
                De la cagnotte restante, les Stake Pools récupèrent <b>leurs frais variables</b> (généralement entre 1 et 5%).
            </li>
            <li>
                Le reste de la cagnotte est partagée entre les différents délégateurs (dont fait partie la Stake Pool elle-même avec son pledge),
                au prorata des ADAs que chacun lui aura délégué.
            </li>
        </ol>

        <p>
            Notez que si les Stake Pools sont responsable du minage des blocs, elles ne sont pas responsable de la distribution des récompenses: celle-ci
            est directement traitée par la blockchain.
        </p>

        <h2>La désirabilité d'une Stake Pool</h2>

        <p>
            Si vous êtes le premier type d'investisseur (court-termiste), le facteur qui vous importe le plus dans votre choix est la
            <b>désirabilité</b> d'une Stake Pool. Elle se considère donc à partir des facteurs suivants:
        </p>

        <ul>
            <li>sa <b>fiabilité</b>: capacité à produire efficacement les blocs qui lui sont confiés</li>
            <li>son <b>coût</b>: frais de fonctionnement fixes et variables</li>
            <li>son <b>niveau de saturation</b></li>
            <li>son <b>engagement</b></li>
        </ul>

        <p>Nous allons couvrir ensemble ces différents points</p>

        <h3>La fiabilité d'une Stake Pool</h3>

        <p>
            Si une Stake Pool échoue à produire des blocs (ou produit des blocs déféctueux), sa probabilité de se voir attribuer à nouveau
            le <b>Slot Lead</b> (capacité à produire le prochain bloc) descend. Il est donc primordial de choisir une Stake Pool gérée par
            une équipe de professionnels.
        </p>

        <h3>Le coût d'une Stake Pool</h3>

        <p>
            Chaque Stake Pool définit, au moment de son enregistrement sur la blockchain (ou d'une mise à jour de son enregistrement) ses
            coûts de fonctionnement. Ils se divisent en 2 parties: le coût <b>fixe</b> (censé couvrir les frais de fonctionnement de la Stake Pool:
            machines, etc), qui est situé à un <b>minimum de 340 ADAs par époque</b>, et le coût variable, qui est le <b>gain</b> ou <b>l'incentive</b>
            de la Stake pool (généralement situé entre 1% et 5%).
        </p>

        <p>
            Si ce facteur est souvent le premier regardé par les délégateurs, il n'est pas si impactant que ça.
            Evitez les Stake Pools avec des coûts fixes ou variables qui vous semblent déraisonnablement importants, mais privilégiez les
            Stake Pools professionnelles avant tout.
        </p>

        <h3 id="stake-pool-saturation">La saturation d'une Stake Pool</h3>

        <p>
            La <b>saturation</b> est l'un des facteurs les plus importants à considérer. Cardano ne veut pas que les Stake Pools puissent
            récolter trop d'enjeu, et devenir trop massives, sinon la blockchain se retrouvait de facto centralisée.
            Ouroboros (le protocole) définit à cette fin une limite de saturation (actuellement fixée à 1% des ADAs mis en jeu sur le réseau, autour
            de 200 millions d'ADAs). Passé cette limite, la Stake Pool n'augmentera plus ses probabilité de droit de création de bloc
            (et donc, ses récompenses). Ses délégateurs auront donc tout intérêt à aller se trouver une autre Stake Pools.
        </p>

        <p>
            Veillez donc à ne pas choisir une Stake Pool saturée ou proche de la saturation. Vous pouvez voir la saturation des Stake Pool sur
            <a href="https://adapools.org">Adapools</a>, entre autres.
        </p>

        <h3>L'engagement d'une Stake Pool</h3>

        <p>
            L'engagement, ou <b>pledge</b>, est le dernier facteur à considérer dans la désirabilité d'une Stake Pool.
            Il indique combien d'ADAs ont été engagés par la Stake Pool dans le processus de validation.
            En effet, le <a href="/doc-cardano/qu-est-ce-que-le-delegated-proof-of-stake-dpos">Delegated Proof of Stake</a> se base sur l'enjeu,
            les Stake Pools ayant investi plus que les autres ont moins de probabilité d'être, ou de devenir malveillantes (même s'il
            existe d'autres sécurité du réseau).
        </p>

        <p>
            Pour les curieux, voici la formule exacte des récompenses d'une Stake Pool:
        </p>

        <img v-lazy-load data-src="/img/tuto-choose-sp/pledge_formula_transparent.webp" alt="Formule des récompenses d'une Stake Pool" />

        <p>Avec:</p>
        <ul class="simple">
            <li>
                <p>R - somme des récompenses disponibles pour cette époque</p>
            </li>
            <li>
                <p>a<sub>0</sub> - Facteur d'influence du pledge (peut être entre 0 et l'infini)</p>
            </li>
            <li>
                <p>z<sub>0</sub> - Saturation relative de la Stake Pool (basée sur le nombre de pools désirée, actuellement k=1000)</p>
            </li>
            <li>
                <p>σ - Enjeu total collecté par la Stake Pool</p>
            </li>
            <li>
                <p>σ’ = min(σ, z<sub>0</sub>) - σ, mais au maximum z<sub>0</sub></p>
            </li>
            <li>
                <p>s - Engagement initial des propriétaires de la Stake Pool</p>
            </li>
            <li>
                <p>s’ = min(s, z<sub>0</sub>) - s, mais au maximum z<sub>0</sub></p>
            </li>
        </ul>


        <p>
            Une pool qui a récolté beaucoup d'enjeu mais qui possède un engagement faible (en proportion) peut donc se retrouver moins
            intéressante qu'une pool qui a récolté moins d'enjeu mais s'engage plus.
        </p>

        <h2><u>La methode de sélection empirique</u>: pour collecter plus d'ADAs</h2>

        <p>
            Même quand on connait les différents paramètres en jeu, la formule et la procédure, il reste compliqué de déterminer
            précisément quelle stake pool va rapporter le plus.
            Voici donc la procédure que je recommanderais pour un maximum d'intérêts à la délégation:
        </p>

        <ol>
            <li>Créez vous un fichier dans lequel vous allez noter vos pools et leurs résultats</li>
            <li>Créez 5 ou 6 portefeuilles sur lesquels vous répartissez équitablement vos fonds</li>
            <li>Sélectionnez les Stake Pools qui répondent à tous les critères précédents, aux frais relativement bas et qui
                fournissent un ratio <b>engagement/délégation</b> élevé</li>
            <li>Déleguez à chacune de ces Pools</li>
            <li>Après 4 époques, faites la moyenne des récompenses reçues pour chaque pool. Eliminez les 2 ou 3 moins bonnes, et recommencez la procédure</li>
        </ol>

        <p>
            Si votre objectif est simplement de collecter le plus d'ADAs possible (ce qui, nous allons voir ensuite, n'est pas nécessairement
            la meilleur stratégie pour vous enrichir), vous devriez en quelques mois avoir un set de pools efficace, voire très efficace.
        </p>

        <h2><u>La méthode de support</u>: pour maximiser la valeur d'ADA</h2>
        <p>
            Si vous collectez et gardez précieusement vos ADAs sur le long-terme, c'est probablement parce que vous êtes convaincu, comme nous,
            que c'est un actif qui a beaucoup de potentiel, et qui risque bien de prendre beaucoup de valeur sur le long terme. Et un HOLDer
            long-terme pourra avoir une stratégie tout à fait différente: <u>il ne joue pas au même jeu</u>.
        </p>

        <p>
            C'est précisément dans ce cas que votre stratégie peut changer fondamentalement: Si ADA est un actif qui peut faire x50 ou x100 sur
            les 5 prochaines années, <b>ne serait-il alors pas plus intéressant d'accélérer cette montée en puissance plutôt que de récupérer
                quelques miettes supplémentaires ?</b>
        </p>

        <p>
            C'est précisément ce que vous pouvez faire en misant sur <b>les stake pools qui contribuent activement au développement de Cardano</b>.
            Il existe un <b>certain nombre de projets dont l'objectif est d'accélérer le développement et l'adoption de
                Cardano et qui se rémunèrent via les Stake Pools</b>. Vous trouverez une liste plus bas.
        </p>

        <p>
            <u>Prenons un exemple très concret</u> pour bien comprendre la situation: Mettons que vous possediez, par exemple, <b>10 000 ADAs</b>,
            (environ 1000€ au moment) et que vous souhaitiez les investir. Vous avez le choix entre 2 pools (a ratio égaux de luck: nombre de blocs attribués / nombre
            de blocs attendus). La première est une pool aux frais bas:
        </p>

        <img v-lazy-load data-src="/img/tuto-choose-sp/pool_1pct.webp" title="Stake pool 1%" />

        <p>
            La seconde est une pool qui contribue (celle-ci est la pool du projet AdaLite).
            Notez au passage qu'elle a un <b>engagement</b> de seulement 50k ADAs contre 750k pour CardanoLand.
        </p>

        <img v-lazy-load data-src="/img/tuto-choose-sp/pool_adalite.webp" title="Stake pool ADALite" />

        <p>
            Si vous prenez la pool qui vous rapporte le plus d'intérêt (la première), vous pouvez espérer gagner environ <b>0.08%</b> de plus par an.
        </p>

        <p>
            Au bout de 5 ans avec des <b>intérêts cumulés</b>, vous aurez <b>12983 ADAs</b> (10000x1.0536<sup>5</sup>) avec la première pool et
            <b>12933 ADAs</b> (10000x1.0528<sup>5</sup>) avec la seconde, soit <b>50 ADAs de moins</b> (<b>0.38%</b> du capital).
            Mais, dans les deux cas, quelle sera la valeur d'un ADA ?
        </p>

        <p>
            Posez vous la question ainsi: Est-ce que votre support infaillible et régulier au projet AdaLite, durant 5 ans, permettant d'intégrer à
            ce portefeuille les features nécessaires et attendues, ou d'en faire la communication ou le support, ne pourrait-il pas favoriser l'adoption
            de Cardano de <b>0.38%</b> ? Ne pourrait-il pas inciter d'autres à venir supporter Adalite ?
            <b>Quelle sera la valeur d'ADA dans 5 ans si AdaLite n'existait plus ? Ne sera-t'elle pas au moins <b>0.38%</b> inférieure ?</b>
        </p>

        <h3>Les Stake Pools qui servent Cardano</h3>

        <p>
            Voici une liste (non-exhaustive) des Stake Pools qui servent Cardano et quelques détails sur leurs projets. Recherchez leur ticker (identifiant court)
            au moment de choisir une Stake Pool, sur les différents wallets.
        </p>

        <ul>
            <li>
                <b>SPFR</b>: <u>Stakepool.fr: Bien sûr que nous y sommes</u>. Notre mission est de démocratiser Cardano en France et dans le monde
                francophone, en traduisant, documentant, et répondant aux questions. Nous sommes une petite pool, et votre soutien nous est très précieux.
            </li>
            <li>
                <b>SHOP</b>: Projet <a href="https://github.com/uniVocity/free-commerce">Free Commerce</a>: construire un portefeuille compatible avec Cardano et les sites de e-commerce, pour pouvoir
                acheter directement en ADA.
            </li>
            <li>
                <b>ADLT</b> ou <b>ADLT2</b> ou <b>ADLT3</b>: <a href="https://adalite.io/">AdaLite</a>: portefeuille compatible Cardano, connectable depuis un cold wallet (comme Trezor ou Ledger Nano X ou S).
            </li>
            <li>
                <b>YOROI</b>: <a href="https://yoroi-wallet.com/#/">Le portefeuille Yoroi</a>, compatible Ledger Nano S ou X.
            </li>
            <li>
                <b>POOLS</b>: <a href="https://adapools.org">Adapools</a>: référence et collecte des statistiques sur les différentes Stake Pool, fournit
                des outils à la communauté des Stake Pools Opérators.
            </li>
            <li>
                <b>LOVE</b> ou <b>PEGA</b> ou <b>SKY</b>: <a href="https://pooltool.io">PoolTool.io</a>: Comme Adapools, référence et collecte des statistiques
                sur les différentes Stake Pool, fournit des outils à la communauté des Stake Pools Opérators.
            </li>
            <li>
                <b>DAVID</b>: Youtubeur anglophone <b>David Likes Crypto</b>, qui crée du contenu anglophone sur Cardano.
            </li>
        </ul>

        <p>
            Il y a bien sûr les pools d'IOHK, l'entreprise contractée pour développer Cardano, mais celle-ci semble déjà recevoir énormément de support, je ne
            l'ai pas incluse dans cette liste. Si vous en connaissez d'autres, <a href="/contact">Contactez nous !</a>
        </p>
    </div>
</template>

<script>
import createHeader from "../../js/meta.js";

export default {
    layout: "DocLayout",
    head: createHeader({
        title:
            "Comment choisir une Stake Pool pour déléguer ses ADAs - Stakepool.fr",
        description:
            "Comment choisir une Stake Pool pour maximiser ses récompenses de Staking. Comment choisir une Stake Pool pour favoriser le développement de Cardano. Tout sur les récompenses de staking de Cardano.",
        image: "http://stakepool.fr/img/tuto-daedalus/stakepool-rewards.webp"
    })
};
</script>